import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectOption } from '../../../../common/inputs/select/model/select-option';
import { ToastService } from '../../../../services/toast.service';
import { LoaderService } from '../../../../services/loader.service';
import { OfferRequest } from '../../../../common/components/solar-calculation-result/model/offer-request';
import { OfferStatus } from '../../../../common/models/company/offer-status.enum';
import { AnalysisService } from '../../../../services/analysis.service';
import { finalize } from 'rxjs';
import { UnsubscribeOnDestroyAdapter } from '../../../../common/adapters/unsubscibe-on-destroy.adapter';
import { TranslocoService } from '@ngneat/transloco';
import { OfferUpdateRequest } from '../../../../common/models/analysis/offer-update-request';

@Component({
  selector: 'psc-edit-offer-request',
  templateUrl: './edit-offer-request.component.html',
})
export class EditOfferRequestComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  @Input() offerRequest: OfferRequest;
  @Output() offerSaved: EventEmitter<OfferRequest> = new EventEmitter<OfferRequest>();
  formGroup: FormGroup;

  statusOptions: SelectOption[] = Object.values(OfferStatus).map((role) => ({ value: role, label: role }));

  boolValues = [
    { prop: 'eligibleForGridConnection', translationKey: 'eligible_for_grid_connection' },
    { prop: 'optimalPowerLessThanConnectedPower', translationKey: 'optimal_power_less_than_connected_power' },
    { prop: 'contactByCompanyAccepted', translationKey: 'contact_by_company_accepted' },
    { prop: 'loanFinancingForSolarPlantDesired', translationKey: 'loan_financing_for_solar_plant_desired' },
    { prop: 'conceptualProjectNeeded', translationKey: 'conceptual_project_needed' },
    {
      prop: 'companyAuthorizedForNextStepInConnection',
      translationKey: 'company_authorized_for_next_step_in_connection',
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private analysisService: AnalysisService,
    private translocoService: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    const minValidator = Validators.min(0);
    this.formGroup = this.formBuilder.group({
      offerAmount: ['', minValidator],
      offerStatus: [OfferStatus.WAITING],
    });

    this.subs.sink = this.formGroup.controls['offerStatus'].valueChanges.subscribe((value: OfferStatus) => {
      if ([OfferStatus.OFFER, OfferStatus.DONE].includes(value)) {
        this.formGroup.controls['offerAmount'].setValidators([Validators.required, minValidator]);
      } else {
        this.formGroup.controls['offerAmount'].setValidators([minValidator]);
      }
      this.formGroup.controls['offerAmount'].updateValueAndValidity();
    });

    if (this.offerRequest) {
      this.formGroup.patchValue(this.offerRequest);
    }
  }

  onSubmit() {
    if (!this.formGroup.valid) {
      return;
    }
    this.loaderService.showLoader();
    const request: OfferUpdateRequest = {
      offerAmount: this.formGroup.value.offerAmount,
      offerStatus: this.formGroup.value.offerStatus,
      analysisId: this.offerRequest.analysisId,
    };
    this.analysisService.updateOfferRequest(request).pipe(
      finalize(() => {
        this.loaderService.hideLoader();
      }),
    )
      .subscribe({
        next: (offer) => {
          this.toastService.showSuccessToast();
          this.offerSaved.emit(offer);
        },
      });
  }

}
